import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  signal,
  ViewChild
} from '@angular/core';

import { FormControl, ReactiveFormsModule, type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { 
  debounceTime, 
  distinctUntilChanged, 
  startWith, 
  takeUntil
} from 'rxjs';
import { Subject } from 'rxjs';
import { 
  type VirtualAutocompleteConfig,
  type VirtualAutocompleteSelectionChange,
  DEFAULT_VIRTUAL_AUTOCOMPLETE_CONFIG
} from './virtual-autocomplete.interfaces';

@Component({
    selector: 'app-virtual-autocomplete',
    imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    ScrollingModule
],
    templateUrl: './virtual-autocomplete.component.html',
    styleUrls: ['./virtual-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: VirtualAutocompleteComponent,
            multi: true
        }
    ]
})
export class VirtualAutocompleteComponent<T = any> implements OnInit, OnDestroy, ControlValueAccessor {
  // #region Inputs
  @Input() data: T[] = [];
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() config: Partial<VirtualAutocompleteConfig<T>> = {};
  @Input() floatLabel: 'always' | 'auto' = 'always';
  @Input() ariaLabel: string = '';
  // #endregion

  // #region Outputs
  @Output() selectionChange = new EventEmitter<VirtualAutocompleteSelectionChange<T>>();
  @Output() cleared = new EventEmitter<void>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  // #endregion

  // #region ViewChildren
  @ViewChild('virtualScrollViewport') virtualScrollViewport!: CdkVirtualScrollViewport;
  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
  // #endregion

  // #region Signals and State
  private readonly destroy$ = new Subject<void>();
  
  // Single form control - let Material handle it
  readonly control = new FormControl<T | null>(null);
  
  
  // Current value signal - tracks control value for proper reactivity
  private readonly currentValue = signal<T | null>(null);
  
  // Search text for filtering - derived from control value changes
  readonly searchText = signal<string>('');

  // Computed configuration with defaults
  readonly effectiveConfig = computed<Required<VirtualAutocompleteConfig<T>>>(() => ({
    ...DEFAULT_VIRTUAL_AUTOCOMPLETE_CONFIG,
    ...this.config
  }));

  // Filtered data based on search text
  readonly filteredData = computed(() => {
    const data = this.data;
    const search = this.searchText() || '';
    const config = this.effectiveConfig();
    
    if (!search) return data;
    return config.filterFn(data, search);
  });

  // Whether to show clear button
  readonly showClearButton = computed(() => {
    const config = this.effectiveConfig();
    const value = this.currentValue();
    
    return config.showClearButton && (
      // Show if there's a selected object
      (value && typeof value === 'object') ||
      // Show if there's typed text
      (typeof value === 'string' && value.trim().length > 0)
    );
  });

  // Dynamic styles based on configuration
  readonly viewportHeight = computed(() => {
    const config = this.effectiveConfig();
    // Show 5 items by default
    return `${config.itemSize * 5}px`;
  });

  readonly optionMinHeight = computed(() => {
    const config = this.effectiveConfig();
    return `${config.itemSize}px`;
  });

  readonly optionLineHeight = computed(() => {
    const config = this.effectiveConfig();
    // Line height is typically half of item size for good typography
    return `${config.itemSize / 2}px`;
  });

  // #endregion

  // #region ControlValueAccessor
  private onChange = (_: T | null) => {};
  private onTouched = () => {};

  writeValue(value: T | null): void {
    this.control.setValue(value, { emitEvent: false });
    this.currentValue.set(value);
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }
  // #endregion

  constructor() {}

  ngOnInit(): void {
    // Listen to control value changes for form integration and filtering
    this.control.valueChanges
      .pipe(
        startWith(null),
        debounceTime(this.effectiveConfig().debounceTime),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => {
        // Update current value signal
        this.currentValue.set(value);
        
        // Emit value change to parent form
        this.onChange(value);
        
        // Update search text for filtering
        // If value is string, user is typing; if object, user selected
        if (typeof value === 'string') {
          this.searchText.set(value);
        } else if (value === null) {
          this.searchText.set('');
        } else {
          // User selected an object, clear search text to show all options when reopened
          this.searchText.set('');
        }
        
        // Emit selection change event
        this.selectionChange.emit({
          value: typeof value === 'string' ? null : value,
          source: typeof value === 'string' ? 'user' : 'user'
        });
        

      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // #region Event Handlers
  onOptionSelected(): void {
    // Material autocomplete will handle setting the control value
    this.onTouched();
  }

  onClear(event: Event): void {
    event.stopPropagation();
    this.control.setValue(null);
    this.cleared.emit();
    this.onTouched();
    
    // Focus the input after clearing
    if (this.inputElement) {
      this.inputElement.nativeElement.focus();
    }
  }

  onAutocompleteOpened(): void {
    this.resetVirtualScroll();
    this.opened.emit();
  }

  onAutocompleteClosed(): void {
    this.closed.emit();
  }

  onInputBlur(): void {
    this.onTouched();
  }
  // #endregion

  // #region Utility Methods
  /**
   * Resets the virtual scroll viewport size calculation
   */
  resetVirtualScroll(): void {
    if (this.virtualScrollViewport) {
      this.virtualScrollViewport.checkViewportSize();
    }
  }

  /**
   * Track by function for virtual scroll performance
   */
  trackByFn = (index: number, item: T): any => {
    return this.effectiveConfig().trackByFn(index, item);
  };

  /**
   * Display function for autocomplete options
   */
  displayFn = (item: T | null): string => {
    return item ? this.effectiveConfig().displayFn(item) : '';
  };

  /**
   * Focus the input element
   */
  focus(): void {
    if (this.inputElement) {
      this.inputElement.nativeElement.focus();
    }
  }


  // #endregion
}